import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Building2, UserPlus, Trash2, ExternalLink, ArrowLeft } from "lucide-react";
import { adminApi } from "../../lib/api";

const SPONSOR_TYPES = [
  ["church", "Church"],
  ["christian_retailer", "Christian Retailer"],
  ["bookstore", "Bookstore / Gift Store"],
  ["ministry", "Ministry"],
  ["school", "Christian School"],
  ["nonprofit", "Nonprofit"],
  ["publisher", "Publisher / Media"],
  ["conference", "Conference / Event"],
  ["corporate_partner", "Corporate Partner"],
  ["other", "Other"],
];

export default function AdminSponsorManagePage() {
  const { sponsor_id } = useParams();
  const [sponsor, setSponsor] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [invites, setInvites] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  const reload = async () => {
    const [s, a, i] = await Promise.all([
      adminApi.getSponsor(sponsor_id),
      adminApi.listAdmins(sponsor_id),
      adminApi.listInvites(sponsor_id),
    ]);
    setSponsor(s);
    setForm({
      sponsor_name: s.sponsor_name,
      sponsor_type: s.sponsor_type,
      logo_url: s.logo_url || "",
      primary_color: s.primary_color || "#4F6F52",
      secondary_color: s.secondary_color || "",
      audience_size: s.audience_size ?? "",
      contact_name: s.contact_name || "",
      contact_email: s.contact_email || "",
      contact_phone: s.contact_phone || "",
      website: s.website || "",
      start_date: s.start_date || "",
      notes: s.notes || "",
      status: s.status,
    });
    setAdmins(a);
    setInvites(i);
  };

  useEffect(() => {
    reload().catch((e) => toast.error(e?.response?.data?.detail || "Load failed"));
  }, [sponsor_id]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.audience_size === "") delete payload.audience_size;
      else payload.audience_size = parseInt(payload.audience_size, 10);
      if (!payload.secondary_color) delete payload.secondary_color;
      // Empty strings on optional fields become null on the server, keeping
      // the DB honest and avoiding "" leaking into the SponsorOut response.
      ["contact_name", "contact_email", "contact_phone", "website", "start_date", "notes"].forEach(
        (k) => {
          if (payload[k] === "" || payload[k] == null) payload[k] = null;
        }
      );
      // Logo is managed by the upload/remove endpoints, not editable inline.
      delete payload.logo_url;
      await adminApi.editSponsor(sponsor_id, payload);
      toast.success("Saved.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const invite = async (e) => {
    e.preventDefault();
    if (!inviteEmail) return;
    try {
      await adminApi.invite(sponsor_id, inviteEmail);
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invite failed");
    }
  };

  const revoke = async (user_id) => {
    if (!window.confirm("Revoke this sponsor admin's access?")) return;
    try {
      await adminApi.revokeAdmin(sponsor_id, user_id);
      toast.success("Access revoked");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Revoke failed");
    }
  };

  if (!sponsor || !form) return <div className="text-[#6B6B6B]">Loading…</div>;
  const publicUrl = `${window.location.origin}/sponsor/${sponsor.sponsor_slug}`;

  return (
    <div data-testid="admin-sponsor-manage-page" className="space-y-8 fade-in-up">
      <Link
        to="/admin/sponsors"
        className="inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] hover:text-[#4F6F52]"
      >
        <ArrowLeft size={14} /> All sponsors
      </Link>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <Building2 size={16} className="text-[#4F6F52]" />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4F6F52]">
            Sponsor
          </span>
        </div>
        <h1 className="font-display text-3xl">{sponsor.sponsor_name}</h1>
        <div className="text-xs text-[#6B6B6B] mt-1">
          <code className="text-[#4F6F52]">{publicUrl}</code>{" "}
          <a href={publicUrl} target="_blank" rel="noopener" className="inline-block">
            <ExternalLink size={12} className="inline text-[#6B6B6B]" />
          </a>
          {" · "}
          <Link
            to={`/sponsor-dashboard/${sponsor.sponsor_id}`}
            className="text-[#4F6F52] underline underline-offset-2"
          >
            Open dashboard
          </Link>
        </div>
      </div>

      {/* -- Details form ---------------------------------------------- */}
      <form
        onSubmit={save}
        data-testid="sponsor-edit-form"
        className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-4"
      >
        <h2 className="font-display text-xl">Details</h2>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Sponsor name">
            <input
              data-testid="edit-name"
              required
              value={form.sponsor_name}
              onChange={(e) => setForm({ ...form, sponsor_name: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            />
          </Field>
          <Field label="Type">
            <select
              data-testid="edit-type"
              value={form.sponsor_type}
              onChange={(e) => setForm({ ...form, sponsor_type: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            >
              {SPONSOR_TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Logo">
            <div className="flex items-center gap-3">
              {sponsor.logo_url && (
                <img
                  data-testid="manage-logo-preview"
                  src={`${process.env.REACT_APP_BACKEND_URL}${sponsor.logo_url}`}
                  alt=""
                  className="h-12 w-12 rounded-lg border border-[#DCE5DB] object-contain bg-white p-1"
                />
              )}
              <label className="inline-flex items-center gap-1.5 text-sm text-[#4F6F52] cursor-pointer underline underline-offset-2">
                <input
                  data-testid="manage-logo-file"
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    try {
                      await adminApi.uploadLogo(sponsor_id, f);
                      toast.success("Logo uploaded");
                      reload();
                    } catch (err) {
                      toast.error(err?.response?.data?.detail || "Upload failed");
                    }
                  }}
                />
                {sponsor.logo_url ? "Replace logo" : "Upload logo"}
              </label>
              {sponsor.logo_url && (
                <button
                  type="button"
                  data-testid="manage-logo-remove"
                  onClick={async () => {
                    if (!window.confirm("Remove logo?")) return;
                    await adminApi.removeLogo(sponsor_id);
                    toast.success("Logo removed");
                    reload();
                  }}
                  className="text-xs text-[#7C3E33] hover:text-[#5A2E27] underline underline-offset-2"
                >
                  Remove
                </button>
              )}
            </div>
          </Field>
          <Field label="Audience size">
            <input
              data-testid="edit-audience"
              type="number"
              min={0}
              value={form.audience_size}
              onChange={(e) => setForm({ ...form, audience_size: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            />
          </Field>
          <Field label="Primary color">
            <input
              type="color"
              value={form.primary_color}
              onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
              className="w-full h-10 rounded-xl border border-[#DCE5DB] bg-white"
            />
          </Field>
          <Field label="Secondary color">
            <input
              type="color"
              value={form.secondary_color || "#DCE5DB"}
              onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
              className="w-full h-10 rounded-xl border border-[#DCE5DB] bg-white"
            />
          </Field>
          <Field label="Contact name">
            <input
              value={form.contact_name}
              onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            />
          </Field>
          <Field label="Contact email">
            <input
              type="email"
              value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            />
          </Field>
          <Field label="Contact phone">
            <input
              data-testid="edit-contact-phone"
              type="tel"
              value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              placeholder="(555) 123-4567"
            />
          </Field>
          <Field label="Website">
            <input
              data-testid="edit-website"
              type="url"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
              placeholder="https://example.org"
            />
          </Field>
          <Field label="Start date">
            <input
              data-testid="edit-start-date"
              type="date"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            />
          </Field>
          <Field label="Sponsor code (unique)">
            <input
              readOnly
              value={sponsor.referral_code}
              className="w-full bg-[#F0EDE7] border border-[#DCE5DB] rounded-2xl px-4 py-2.5 text-[#4F6F52] font-mono"
            />
          </Field>
          <Field label="Landing page identifier">
            <input
              readOnly
              value={sponsor.sponsor_slug}
              className="w-full bg-[#F0EDE7] border border-[#DCE5DB] rounded-2xl px-4 py-2.5 text-[#4F6F52] font-mono"
            />
          </Field>
          <Field label="Status">
            <select
              data-testid="edit-status"
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
            >
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </select>
          </Field>
        </div>
        <Field label="Notes (internal)">
          <textarea
            data-testid="edit-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5 resize-none"
            placeholder="Internal notes — never shown publicly."
          />
        </Field>
        <button
          data-testid="edit-save"
          type="submit"
          disabled={saving}
          className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-5 py-2.5 text-sm disabled:opacity-60"
        >
          {saving ? "Saving…" : "Save changes"}
        </button>
      </form>

      {/* -- Admins section -------------------------------------------- */}
      <section className="rounded-3xl border border-[#DCE5DB] bg-white p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl">Sponsor admins</h2>
          <span className="text-xs text-[#6B6B6B]">{admins.length} admin(s)</span>
        </div>

        <form onSubmit={invite} className="flex flex-wrap gap-2">
          <input
            data-testid="invite-email"
            type="email"
            required
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            placeholder="admin@sponsor.example"
            className="flex-1 min-w-[240px] bg-white border border-[#DCE5DB] rounded-2xl px-4 py-2.5"
          />
          <button
            data-testid="invite-send"
            type="submit"
            className="bg-[#4F6F52] hover:bg-[#3F5A42] text-white rounded-full px-4 py-2.5 text-sm inline-flex items-center gap-1.5"
          >
            <UserPlus size={14} /> Invite
          </button>
        </form>

        {admins.length === 0 ? (
          <div className="text-sm text-[#6B6B6B]" data-testid="no-admins">
            No sponsor admins bound yet.
          </div>
        ) : (
          <ul className="divide-y divide-[#DCE5DB]/60">
            {admins.map((a) => (
              <li
                key={a.id}
                data-testid={`admin-row-${a.user_id}`}
                className="py-3 flex items-center justify-between"
              >
                <div>
                  <div className="text-sm">{a.email}</div>
                  <div className="text-[11px] text-[#6B6B6B]">
                    {a.role} · added {a.created_at.slice(0, 10)}
                  </div>
                </div>
                <button
                  data-testid={`revoke-${a.user_id}`}
                  onClick={() => revoke(a.user_id)}
                  className="text-xs text-[#7C3E33] hover:text-[#5A2E27] inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Revoke
                </button>
              </li>
            ))}
          </ul>
        )}

        {invites.filter((i) => i.status === "pending").length > 0 && (
          <div className="pt-2 border-t border-[#DCE5DB]/60">
            <h3 className="text-[11px] uppercase tracking-[0.14em] text-[#6B6B6B] mb-2">
              Pending invitations
            </h3>
            <ul className="space-y-1 text-sm text-[#6B6B6B]">
              {invites
                .filter((i) => i.status === "pending")
                .map((i) => (
                  <li key={i.id}>
                    {i.email} — expires {i.expires_at.slice(0, 10)}
                  </li>
                ))}
            </ul>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-xs text-[#6B6B6B] mb-1.5">{label}</div>
      {children}
    </label>
  );
}
